import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Icon, Text, TextInput, Title } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import UserListRow from "components/custom/userListRow";
import DemoDataToggle from "components/custom/demoDataToggle";
import { demoPeople } from "features/community/mockData/social";
import { COMMUNITY_PEOPLE } from "constants/app-paths";
import styles from "./ConnectionList.module.less";

const ConnectionSkeleton = () => (
  <div className={styles.listCard} aria-label="Loading connections">
    {Array.from({ length: 5 }).map((_, index) => <div key={index} className={styles.skeletonRow}><span /><div><i /><i /></div></div>)}
  </div>
);

const EmptyState = ({ title, description, searching, onBrowse }) => (
  <div className={styles.emptyState}>
    <span className={styles.emptyIcon}><Icon icon="FiUsers" size={21} /></span>
    <Title level={4}>{searching ? "No matching learners found" : title}</Title>
    <Text>{searching ? "Try a name, username, education level, or specialty." : description}</Text>
    <Button type="default" text="Browse people" onClick={onBrowse} />
  </div>
);

const ConnectionList = ({ title, trigger, isFetching, listType }) => {
  const { userCode } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageRef = useRef(1);

  const load = useCallback(async (reset) => {
    const nextPage = reset ? 1 : pageRef.current + 1;
    if (!reset) setIsLoadingMore(true);
    setRequestError(false);
    const result = await trigger({ userCode, q: q || undefined, page: nextPage });
    const response = result?.data;
    if (!response) {
      if (reset) setRequestError(true);
      setHasLoaded(true);
      setIsLoadingMore(false);
      return;
    }
    const rows = (response.data || []).filter((user) => user.user_code !== userCode);
    setItems((current) => reset ? rows : [...current, ...rows]);
    setCount(response.count || 0);
    pageRef.current = nextPage;
    setHasLoaded(true);
    setIsLoadingMore(false);
  }, [q, trigger, userCode]);

  useEffect(() => {
    const timeout = window.setTimeout(() => load(true), 350);
    return () => window.clearTimeout(timeout);
  }, [load]);

  const displayedItems = useDemoData
    ? demoPeople.filter((user) => [user.name, user.username, user.bio, ...(user.specialties || [])].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase()))
    : items;
  const hasMore = !useDemoData && items.length < count;
  const searching = Boolean(q.trim());
  const isInitialLoading = !useDemoData && !hasLoaded && !requestError;
  const isRefreshing = !useDemoData && hasLoaded && isFetching && !isLoadingMore;
  const emptyTitle = listType === "followers" ? "No followers yet" : "You're not following anyone yet";
  const emptyDescription = listType === "followers"
    ? "When other learners follow you, they will appear here."
    : "Follow learners and educators to keep up with their study activity.";
  const pageDescription = listType === "followers"
    ? "Learners and educators who follow your learning journey."
    : "People whose learning activity you follow.";

  return (
    <PageLayout backIcon label={<Title level={3}>{title}</Title>}>
      <main className={styles.connectionPage}>
        <header className={styles.header}>
          <div>
            <Title level={3}>{title}</Title>
            <Text>{pageDescription}</Text>
          </div>
        </header>
        <div className={styles.searchWrap}>
          <Icon icon="FiSearch" size={17} />
          <TextInput placeholder="Search by name, username, education, or specialty" value={q} handleChange={(event) => setQ(event?.target?.value || "")} />
          {q && <button type="button" aria-label="Clear search" onClick={() => setQ("")}><Icon icon="IoMdClose" size={16} /></button>}
        </div>
        {isInitialLoading ? <ConnectionSkeleton /> : requestError ? (
          <div className={styles.emptyState}><Title level={4}>Connections could not be loaded</Title><Text>Please try again.</Text><Button type="default" text="Retry" onClick={() => load(true)} /></div>
        ) : displayedItems.length === 0 ? <EmptyState title={emptyTitle} description={emptyDescription} searching={searching} onBrowse={() => navigate(COMMUNITY_PEOPLE)} /> : (
          <>
            <div className={styles.listCard} aria-live="polite">
              {isRefreshing && <span className={styles.refreshing}>Updating…</span>}
              {displayedItems.map((user) => <UserListRow key={user.user_code} user={user} listType={listType} showFollow={!useDemoData} />)}
            </div>
            {hasMore && <div className={styles.loadMore}><Button type="default" text="Load more" loading={isLoadingMore} disabled={isFetching} onClick={() => load(false)} /></div>}
          </>
        )}
        {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label={`${title} data`} />}
      </main>
    </PageLayout>
  );
};

export default ConnectionList;
